import React from 'react';
import Helmet from 'react-helmet';
import ClipLoader from 'react-spinners/ClipLoader';
import _ from 'lodash';
import Joi from 'joi-browser';
import moment from 'moment';

import { CcusCompanyForm } from '../components/forms/CcusCompanyForm';
import { PartnerNaviList } from '../components/list/PartnerNaviList';
import { PrimaryCompanyNaviList } from '../components/list/PrimaryCompanyNaviList';
import { MasterPageHeader } from '../components/PageHeader';
import { Confirmation, Information } from '../components/modals/common';
import { CloseEditView } from '../components/CloseEditView';
import {
  ConnectCCUSFailureInfo,
  ConnectCCUSMismatchInfo,
  ConnectCCUSSuccessConfirmation,
  PopupErrorConnectCcus,
  ConfrictOwnerCCUSInfo,
  DifferentCCUSNameConfirmation,
} from '../components/CcusBase';

import { CompanyService } from '../services/Companies';
import { PrimeCCUSPreviewService } from '../services/PrimeCCUSPreview';
import LocalStorageService from '../services/utilities/LocalStorageService';
import SessionStorageService from '../services/utilities/SessionStorageService';
import {
  DATE_TIME_FORMAT,
  HEADER_CONSTANTS,
  STATUS_CODE,
  TIME_OUT_TWO_SECOND,
} from '../utilities/constants';
import {
  isLoginUserPartner,
  isPartnerRoleId,
  isPrimaryCompanyRoleId,
} from '../utilities/roleUtility';
import { messageUtility } from '../utilities/messageUtility';
import regex from '../utilities/validateRegex';
import { setWaitTime, getCompanyId } from '../utilities/commonUtil';
import {
  getPartnerCompanyName,
  propsToCompanyId,
} from '../utilities/partnerCompanyConfirmUtil';
import _masterSlideLoader from '../utilities/masterSlideLoader';
import dialogLoader from '../utilities/dialogLoader';
import { validationRegexByJoi } from '../utilities/commonValidation';
import { CCUS_PAGE, CCUS_STATUS } from './PrimeCcusPreview';

const getMessageOfRole = () => {
  if (isPrimaryCompanyRoleId(LocalStorageService.getRoleId())) {
    return (
      '続けてCCUSへの現場情報の登録および取得、施工体制の登録が可能となる連携ユーザーを設定してください。\n' +
      '就業履歴を送信するだけの場合は不要です。'
    );
  }
  return '続けてCCUSから事業者情報、技能者情報の取得が可能となる連携ユーザーを設定してください。就業履歴を送信するだけの場合は不要です。';
};

class CcusCompany extends React.Component {
  constructor(props) {
    super(props);

    this.ownerCompanyId = getCompanyId();
    this.selectedCompanyId = SessionStorageService.getItem('companyCcusId');
    this.isOwner = this.ownerCompanyId === this.selectedCompanyId;
    this.ccusId = propsToCompanyId(this.props);
    this.isRolePartner = isPartnerRoleId(LocalStorageService.getRoleId());
    this.isPrimaryCompanyRole = isPrimaryCompanyRoleId(
      LocalStorageService.getRoleId()
    );

    this.state = {
      isEditable: false,
      showInformationModal: false,
      statusMessage: null,
      formCompanyValues: {
        id: null,
        ccus_jigyousya_id: '',
        ccus_company_admin_id: '',
        ccus_company_admin_password: '',
        connection_status: null,
        last_connected_at: '',
      },

      isLoading: false,
      selectedCompanyName: '',
      validate: {
        invalid: false,
        messages: {},
      },
      messageError: '',
      isValidateSelect: false,
      isLoadingCcus: false,
      isCCUSFormEditable: true,
      isShowErrorConnectCcus: false,
      isShowCreateSuccess: false,
      isShowPass: false,
      lastConnectedAt: null,
      passwordVisible: false,
      isLoadingData: false,
      hasInitData: false,
      expirationCompany: {},
    };

    this.connectingCcusFlag = false; // CCUS接続確認ボタン連打防止Flag

    this.events = {
      goBack: this.goBack,
    };

    this.goPreviousPage = {
      callback: {
        clear: () => {
          _masterSlideLoader.show('PrimeCcusPreview', {
            state: { id: this.selectedCompanyId },
          });
          dialogLoader.close();
        },
        close: () => {
          dialogLoader.close();
        },
      },
    };

    this.page = this.props.page;
    this.ccus_company_admin_id = null;
  }

  getExpiration = async () => {
    await CompanyService.expirations(this.selectedCompanyId).then(response => {
      if (!response.result) {
        return;
      }
      this.setState({
        expirationCompany: {
          ...this.state.expirationCompany,
          ...response.data.entities.company.expiration,
        },
      });
    });
  };

  componentDidMount() {
    this.getInfo();
    this.getExpiration();
  }

  getInfo = () => {
    if (isLoginUserPartner()) {
      this.setCompanyName();
    }
    this.getBasicInfo();
  };

  getBasicInfo = async () => {
    this.setState({ isLoadingData: true });
    const response = await PrimeCCUSPreviewService.getBasicInfo(
      this.selectedCompanyId
    );
    this.setState({ isLoadingData: false });
    if (response.result) {
      if (this.page === CCUS_PAGE.CcusCompanyCreate) {
        const ccus_jigyousya_id = _.get(
          response.data.entities,
          'ccus_jigyousya_id',
          ''
        );
        const ccus_company_admin = _.get(
          response.data.entities,
          'ccus_company_admin'
        );
        if (ccus_jigyousya_id || !_.isEmpty(ccus_company_admin)) {
          this.ccus_company_admin_id = _.get(
            ccus_company_admin,
            'ccus_company_admin_id',
            ''
          );
          this.setState({
            hasInitData: true,
            formCompanyValues: {
              ...this.state.formCompanyValues,
              ccus_jigyousya_id,
              ccus_company_admin_id: this.ccus_company_admin_id,
            },
          });
        }
        return;
      }
      const {
        id,
        ccus_company_admin_id,
        connection_status,
        last_connected_at,
      } = response.data.entities.ccus_company_admin;
      this.setState({
        formCompanyValues: {
          ...this.state.formCompanyValues,
          id,
          ccus_jigyousya_id: response.data.entities.ccus_jigyousya_id,
          ccus_company_admin_id,
          connection_status,
          last_connected_at,
        },
        lastConnectedAt: last_connected_at,
      });
    }
  };

  goBack = (showDialog = false) => {
    this.setState({ isShowCreateSuccess: false });
    if (showDialog) {
      dialogLoader.show(CloseEditView, this.goPreviousPage);
    } else {
      _masterSlideLoader.show('PrimeCcusPreview', {
        state: { id: this.selectedCompanyId },
      });
    }
  };

  initSchema = () => {
    const isRequiredPass =
      (this.page === CCUS_PAGE.CcusCompanyEdit && this.state.isShowPass) ||
      this.page === CCUS_PAGE.CcusCompanyCreate;
    return Joi.object().keys({
      ccus_jigyousya_id: Joi.string()
        .regex(regex.pattern.integerNoSign)
        .length(14)
        .required(),
      ccus_company_admin_id: Joi.string()
        .regex(regex.pattern.pwd)
        .max(14)
        .required(),
      ccus_company_admin_password: isRequiredPass
        ? Joi.string()
            .regex(regex.pattern.noSpace)
            .max(24)
            .required()
        : Joi.any(),
    });
  };

  setCompanyName = () => {
    this.setState(
      {
        isLoading: true,
      },
      async () => {
        const companyName = await getPartnerCompanyName(this.selectedCompanyId);
        this.setState({ selectedCompanyName: companyName, isLoading: false });
      }
    );
  };

  handleVisibleDialog = isShow => {
    this.setState({ showInformationModal: isShow });
  };

  showStatusDialog = statusMessage => {
    this.handleVisibleDialog(true);
    this.setState({ statusMessage });
  };

  handleOnChange = (e, key) => {
    const { formCompanyValues } = this.state;
    const { value } = e.target;
    this.setState({
      formCompanyValues: {
        ...formCompanyValues,
        [key]: value,
      },
    });
  };

  validate = (e, targetName) => {
    const values = _.cloneDeep(this.state.formCompanyValues);
    const validate = _.cloneDeep(this.state.validate);
    const schema = this.initSchema();

    const result = validationRegexByJoi(schema, values, true);
    if (e || targetName) {
      const name = _.isEmpty(targetName) ? e.target.name : targetName;
      if (result.messages[name]) {
        validate.messages[name] = result.messages[name];
      } else if (validate.messages[name]) {
        delete validate.messages[name];
      }
    }
    validate.invalid = !_.isEmpty(validate.messages);
    this.setState({
      lastConnectedAt: null,
      validate,
    });
    return validate.invalid;
  };

  /**
   * Validate all form data, execute when click create/update button
   * @param {object} values: state data of formCompanyValues
   * @return {boolean} true: valid data, false: invalid data
   */
  validateAll = values => {
    const validate = _.cloneDeep(this.state.validate);
    const schema = this.initSchema();

    const result = validationRegexByJoi(schema, values, true);
    if (!result.status) {
      this.setState({
        validate: {
          invalid: true,
          messages: {
            ...validate.messages,
            ...result.messages,
          },
        },
      });
    }
    return result.status;
  };

  /**
   * onClick create/update button
   * validate data and try connecting to CCUS first, before allow user to submit data
   */
  handleSubmit = async () => {
    const formCompanyValues = _.cloneDeep(this.state.formCompanyValues);
    if (!this.validateAll(formCompanyValues)) {
      return;
    }

    // BLDE_V2-37869: CCUS接続確認ボタン連打防止。ダイアログが開いている間も連打禁止
    if (this.connectingCcusFlag || this.state.isLoadingCcus) {
      return;
    }
    this.connectingCcusFlag = true;

    // 編集時は、一旦登録不可にしない(CCUS情報が任意に削除できるようになったら要修正)
    const isCreateForm =
      this.page === CCUS_PAGE.CcusCompanyCreate && !this.ccus_company_admin_id;
    if (isCreateForm && !this.isOwner) {
      const owner_company_response = await PrimeCCUSPreviewService.getBasicInfo(
        this.ownerCompanyId
      );
      // 現在、自社以外のCCUSID情報を登録できるのは代行関係にある協力会社のみなので、協力会社名でよい
      const owner_company_name = await getPartnerCompanyName(
        this.ownerCompanyId
      );
      if (owner_company_response.result) {
        const owner_ccus_jigyousya_id = _.get(
          owner_company_response.data.entities,
          'ccus_jigyousya_id',
          ''
        );
        if (owner_ccus_jigyousya_id === formCompanyValues.ccus_jigyousya_id) {
          dialogLoader.show(ConfrictOwnerCCUSInfo, {
            state: {
              selectedCompanyName: this.state.selectedCompanyName,
              ownerCompanyName: owner_company_name,
            },
            callback: {
              back: () => {
                dialogLoader.close();
              },
            },
          });
          this.connectingCcusFlag = false;
          return;
        }
      } else {
        this.setState({
          isLoadingCcus: false,
          isShowErrorConnectCcus: true,
          messageError: _.get(owner_company_response.data, 'message'),
        });
        this.connectingCcusFlag = false;
        return;
      }
    }

    if (formCompanyValues.ccus_company_admin_password === '') {
      formCompanyValues.ccus_company_admin_password = undefined;
    }
    this.setState({ isLoadingCcus: true, isCCUSFormEditable: false });
    const response = await PrimeCCUSPreviewService.connectConfirmation(
      this.selectedCompanyId,
      formCompanyValues
    );
    if (response.result) {
      const ccusStateManagementId = _.get(
        response.data,
        'entities.ccus_state_management_id',
        null
      );
      this.monitoringFetchCcusCompanyData(
        ccusStateManagementId,
        this.selectedCompanyId
      );
    } else {
      this.setState({
        isLoadingCcus: false,
        isShowErrorConnectCcus: true,
        messageError: _.get(response.data, 'message'),
      });
    }
    this.connectingCcusFlag = false;
  };

  monitoringFetchCcusCompanyData = (ccusStateManagementId, companyId) => {
    setWaitTime(TIME_OUT_TWO_SECOND).then(async () => {
      const params = {
        ccus_state_management_id: ccusStateManagementId,
      };
      const response = await PrimeCCUSPreviewService.connectCcusFetchData(
        companyId,
        params
      );
      if (response.result) {
        if (response.status === STATUS_CODE.ok) {
          this.setState({
            lastConnectedAt: moment().format(DATE_TIME_FORMAT.slashDateFormat),
            isLoadingCcus: false,
          });
          const data = {
            ccus_jigyousya_id: this.state.formCompanyValues.ccus_jigyousya_id,
            ccus_company_admin_id: this.state.formCompanyValues
              .ccus_company_admin_id,
            responseData: response.data.entities,
          };
          if (data.ccus_jigyousya_id === data.responseData.ccus_jigyousya_id) {
            // Unicode 正規化形式も一致させた、正確な文字列で評価
            const is_match_company_name =
              String(data.responseData.ccus_jigyousya_name).normalize() ===
              String(data.responseData.company_name).normalize();
            const show_dialog = is_match_company_name
              ? ConnectCCUSSuccessConfirmation
              : DifferentCCUSNameConfirmation;
            const isCreateForm =
              this.page === CCUS_PAGE.CcusCompanyCreate &&
              !this.ccus_company_admin_id;
            dialogLoader.show(show_dialog, {
              state: {
                ...data,
                isCreateForm,
              },
              callback: {
                back: () => {
                  dialogLoader.close();
                  this.setState({ isCCUSFormEditable: true });
                },
                done: () => {
                  if (isCreateForm) {
                    this.createCcusBasicInfo(ccusStateManagementId);
                  } else {
                    this.updateCcusBasicInfo(ccusStateManagementId);
                  }
                  dialogLoader.close();
                  this.setState({ isCCUSFormEditable: false });
                },
              },
            });
          } else {
            dialogLoader.show(ConnectCCUSMismatchInfo, {
              state: data,
              callback: {
                done: () => {
                  dialogLoader.close();
                  this.setState({ isCCUSFormEditable: true });
                },
              },
            });
          }
        } else if (response.status === STATUS_CODE.accepted) {
          this.monitoringFetchCcusCompanyData(ccusStateManagementId, companyId);
        }
      } else {
        if (response.status === STATUS_CODE.badRequest) {
          this.setState({ isLoadingCcus: false });
          const messageNG = _.get(response, 'message') || _.get(response.data, 'message')
          const isOwner = this.isOwner
          return dialogLoader.show(ConnectCCUSFailureInfo, {
            state: {
              messageNG,
              isOwner,
            },
            callback: {
              done: () => {
                dialogLoader.close();
                this.setState({ isCCUSFormEditable: true });
              },
            },
          });
        }
        this.setState({
          isLoadingCcus: false,
          isShowErrorConnectCcus: true,
          messageError: _.get(response.data, 'message', null),
        });
      }
    });
  };

  toggleShowPassword = isShowPass => {
    let validate = _.cloneDeep(this.state.validate);
    validate = _.omit(validate, 'messages.ccus_company_admin_password');
    validate.invalid = !_.isEmpty(validate.messages);

    this.setState(prevState => ({
      formCompanyValues: {
        ...prevState.formCompanyValues,
        ccus_company_admin_password: '',
      },
      lastConnectedAt: null,
      validate,
      isShowPass: !isShowPass,
      passwordVisible: false,
    }));
  };

  togglePasswordVisible = passwordVisible => {
    this.setState({
      passwordVisible: !passwordVisible,
    });
  };

  createCcusBasicInfo = async ccusStateManagementId => {
    if (this.state.isLoading) {
      return;
    }
    this.setState({ isLoading: true });
    const { formCompanyValues, lastConnectedAt } = this.state;
    const data = {
      ccus_jigyousya_id: formCompanyValues.ccus_jigyousya_id,
      ccus_company_admin_id: formCompanyValues.ccus_company_admin_id,
      ccus_company_admin_password:
        formCompanyValues.ccus_company_admin_password,
      connection_status: CCUS_STATUS.OK,
      last_connected_at: lastConnectedAt,
      ccus_state_management_id: ccusStateManagementId,
    };

    const response = await PrimeCCUSPreviewService.createBasicInfo(
      this.selectedCompanyId,
      data
    );
    this.setState({ isLoading: false });
    if (response.result) {
      return this.setState({
        isShowCreateSuccess: true,
        formCompanyValues: {
          ...formCompanyValues,
          id: _.get(response.data, 'entities.ccus_company_admin.id'),
        },
      });
    } else {
      this.showStatusDialog(_.get(response.data, 'message'));
    }
  };

  updateCcusBasicInfo = async ccusStateManagementId => {
    if (this.state.isLoading) {
      return;
    }
    this.setState({ isLoading: true });
    const { formCompanyValues, lastConnectedAt } = this.state;
    const data = {
      ccus_jigyousya_id: formCompanyValues.ccus_jigyousya_id,
      ccus_company_admin_id: formCompanyValues.ccus_company_admin_id,
      ccus_state_management_id: ccusStateManagementId,
      connection_status: CCUS_STATUS.OK,
      last_connected_at: lastConnectedAt,
    };
    if (this.state.isShowPass) {
      data.ccus_company_admin_password =
        formCompanyValues.ccus_company_admin_password;
    }

    const response = await PrimeCCUSPreviewService.updateBasicInfo(
      this.selectedCompanyId,
      data
    );
    this.setState({ isLoading: false });
    if (response.result) {
      this.goBack(false);
    } else {
      this.showStatusDialog(response.data.message);
    }
  };

  goToListUser = () => {
    _masterSlideLoader.show(CCUS_PAGE.CcusCompanyUserEdit, {
      state: {
        id: _.get(this.state.formCompanyValues, 'id'),
      },
    });
  };

  /**
   * render connection status text
   * if current data has connected before, show the lastConnectedDate
   * @return {JSX.Element}
   */
  renderConnectionStatus = () => {
    const lastConnectedAt = this.state.lastConnectedAt;
    let lastConnectedDate;
    if (lastConnectedAt) {
      lastConnectedDate = moment(lastConnectedAt).format(
        DATE_TIME_FORMAT.slashDateFormat
      );
    } else {
      lastConnectedDate = (
        <span className='connect-ccus-detail--warning'>未確認</span>
      );
    }
    return <React.Fragment>前回接続確認日 {lastConnectedDate}</React.Fragment>;
  };

  render() {
    const {
      isLoading,
      selectedCompanyName,
      showInformationModal,
      formCompanyValues,
      validate: { messages, invalid },
      isLoadingCcus,
      isCCUSFormEditable,
      isShowErrorConnectCcus,
      messageError,
      isShowCreateSuccess,
      isShowPass,
      isLoadingData,
      passwordVisible,
    } = this.state;
    const title = `${selectedCompanyName}会社情報`;
    const isCreateForm =
      this.page === CCUS_PAGE.CcusCompanyCreate && !this.state.hasInitData;

    return (
      <div id='slide-view' className='contruction-slide-view show CcusCompany'>
        <Helmet>
          <title>
            {HEADER_CONSTANTS.title} - {title}
          </title>
        </Helmet>
        <div className='contruction-slide-view__view'>
          <MasterPageHeader
            loaded={!isLoading}
            loaderComponent={
              <div className='contruction-slide-view__title'>
                <ClipLoader size={20} color={'#EDB715'} />
              </div>
            }
            title={title}
            onClose={() => this.goBack(true)}
          />
          <div className='contruction-slide-view__body contruction-slide-view__body--height-fix'>
            <div className='contruction-detail-view'>
              <div className='contruction-detail-view__sidemenu'>
                {this.isPrimaryCompanyRole && (
                  <PrimaryCompanyNaviList
                    isSelectedCcusManagementInfo={true}
                    isEditable={true}
                    expirationCompany={this.state.expirationCompany}
                  />
                )}
                {this.isRolePartner && (
                  <PartnerNaviList
                    isCcusManagementInfo={true}
                    isEditable={true}
                    selectedPartnerCompanyId={this.selectedCompanyId}
                    expirationCompany={this.state.expirationCompany}
                  />
                )}
              </div>
              <div className='contruction-detail-view__body contruction-detail-view__body--fixed'>
                <div className='contruction-detail-view__menu'>
                  <span className='construction-detail-view__button'>
                    <button
                      className='btn--sub'
                      onClick={() => this.goBack(true)}
                    >
                      {isCreateForm ? '登録をやめる' : '編集をやめる'}
                    </button>
                  </span>
                  <span className='construction-detail-view__button'>
                    <button
                      disabled={invalid}
                      className={invalid ? 'btn--disabled' : 'btn--effective'}
                      onClick={this.handleSubmit}
                    >
                      {isCreateForm ? '登録' : '更新'}
                    </button>
                  </span>
                  <span className='connect-ccus-detail'>
                    {isCreateForm ? '登録' : '更新'}
                    時にCCUSとの接続確認を行います。
                    {!isLoadingData &&
                      !isCreateForm &&
                      this.renderConnectionStatus()}
                  </span>
                </div>
                <div className='contruction-detail-view__bar' />
                <div className='contruction-detail-view__main contruction-detail-view__main--fix contruction-detail-view__main--fixed'>
                  <CcusCompanyForm
                    editPage={this.page === CCUS_PAGE.CcusCompanyEdit}
                    data={formCompanyValues}
                    messageValidate={messages}
                    checkValidate={this.validate}
                    setValue={this.handleOnChange}
                    isLoadingCcus={isLoadingCcus}
                    isCCUSFormEditable={isCCUSFormEditable}
                    toggleShowPassword={this.toggleShowPassword}
                    isShowPass={isShowPass}
                    togglePasswordVisible={this.togglePasswordVisible}
                    passwordVisible={passwordVisible}
                    isOwner={this.isOwner}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <Information
          message={this.state.statusMessage}
          buttonText={messageUtility.getMessage('I-000-011')}
          title={messageUtility.getMessage('E-001-402')}
          visible={showInformationModal}
          onClose={() => this.handleVisibleDialog(false)}
          multilineText={true}
        />

        <PopupErrorConnectCcus
          onClose={() =>
            this.setState({
              isShowErrorConnectCcus: false,
              isCCUSFormEditable: true,
            })
          }
          isShowPopup={isShowErrorConnectCcus}
          message={messageError}
        />

        <Confirmation
          message={getMessageOfRole()}
          title='CCUS基本情報登録完了'
          cancelButtonText={messageUtility.getMessage('I-000-017')}
          confirmButtonText='連携ユーザーを登録'
          visible={isShowCreateSuccess}
          onCancel={() => this.goBack(false)}
          onConfirm={this.goToListUser}
        />
      </div>
    );
  }
}

export class CcusCompanyCreate extends React.Component {
  render() {
    return <CcusCompany {...this.props} page={CCUS_PAGE.CcusCompanyCreate} />;
  }
}

export class CcusCompanyEdit extends React.Component {
  render() {
    return <CcusCompany {...this.props} page={CCUS_PAGE.CcusCompanyEdit} />;
  }
}



// WEBPACK FOOTER //
// ./src/pages/CcusCompany.jsx